import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from "firebase/auth";
import { auth } from "../firebase/config";
import { RxCross2 } from "react-icons/rx";
import { FiLock, FiKey, FiShield, FiEye, FiEyeOff } from "react-icons/fi";
import { getFunctions, httpsCallable } from "firebase/functions";

/* ---------- UI helper (local show/hide state only, no form logic) ---------- */

const PasswordField = ({
  label,
  htmlFor,
  icon: Icon,
  placeholder,
  hasError,
  inputProps,
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex w-full flex-col gap-2">
      <label
        htmlFor={htmlFor}
        className="text-start text-[13px] font-normal text-white/70"
      >
        {label}
      </label>
      <div className="group relative">
        <Icon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-white/60 transition-colors duration-200 group-focus-within:text-[#4268FF]" />
        <input
          id={htmlFor}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          {...inputProps}
          className={`box-border w-full rounded-[15px] border-2 bg-white/[0.08] py-3 pl-12 pr-24 text-[14px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_4px_20px_rgba(0,0,0,0.25)] outline-none transition-all duration-200 placeholder:text-[13px] placeholder:font-normal placeholder:text-white/50 md:py-4 md:text-[15px] md:placeholder:text-[14px] hover:border-white/40 focus:border-[#4268FF] focus:bg-white/[0.12] focus:shadow-[0_0_0_4px_rgba(66,104,255,0.2)] ${
            hasError ? "border-red-500/70" : "border-white/25"
          }`}
        />
        {hasError && (
          <RxCross2 className="absolute right-12 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-red-500 p-[2px] text-white" />
        )}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          {visible ? <FiEyeOff className="text-base" /> : <FiEye className="text-base" />}
        </button>
      </div>
    </div>
  );
};

/* ---------- Component ---------- */

const Securite = () => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
  } = useForm();

  const [currentError, setCurrentError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const functions = getFunctions();
  const handleValidation = () => {
    const values = getValues();
    clearErrors();
    setCurrentError("");
    setSuccessMessage("");

    if (!values.currentPassword) {
      setError("currentPassword", {
        type: "required",
        message: "Le mot de passe actuel est requis",
      });
      setCurrentError("Le mot de passe actuel est requis");
      return false;
    }

    if (!values.newPassword) {
      setError("newPassword", {
        type: "required",
        message: "Un nouveau mot de passe est requis",
      });
      setCurrentError("Un nouveau mot de passe est requis");
      return false;
    }

    if (values.newPassword !== values.confirmPassword) {
      setError("confirmPassword", {
        type: "validate",
        message: "Les mots de passe ne correspondent pas.",
      });
      setCurrentError("Les mots de passe ne correspondent pas.");
      return false;
    }

    return true;
  };
  const sendPasswordResetConfirmationEmail = async (email, name) => {
    try {
      const sendEmailFunction = httpsCallable(
        functions,
        "sendPasswordResetConfirmationEmail"
      );
      const result = await sendEmailFunction({
        email: email,
        name: name,
      });

      console.log("Password reset confirmation email sent:", result.data);
      return result.data;
    } catch (error) {
      console.error("Error sending password reset confirmation email:", error);
    }
  };

  const onSubmit = async (data) => {
    const user = auth.currentUser;
    const { currentPassword, newPassword } = data;

    // Re-authenticate the user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);

    try {
      // Re-authenticate user
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      // Set success message
      setSuccessMessage("Le mot de passe a été mis à jour avec succès");

      // Send confirmation email
      await sendPasswordResetConfirmationEmail(
        user.email,
        user.displayName || user.email.split("@")[0]
      );
    } catch (error) {
      console.error("Error updating password:", error);
      setCurrentError(
        "Erreur lors de la mise à jour du mot de passe : " +
          "L'e-mail ou le mot de passe est erroné"
      );
    }
  };

  return (
    <div className="flex w-full flex-col items-start">
      {/* Banners */}
      {currentError && (
        <div className="mb-4 flex w-full items-center gap-3 rounded-[15px] border border-red-500/30 bg-red-500/10 px-4 py-3 text-start text-[13px] text-red-200 md:w-fit md:text-[14px]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.5356 12.65C13.78 12.8937 13.78 13.2938 13.5356 13.5375C13.2919 13.7813 12.8956 13.7813 12.6513 13.5375L10.0037 10.8875L7.33749 13.5562C7.09124 13.8 6.69252 13.8 6.44627 13.5562C6.20064 13.3062 6.20064 12.9063 6.44627 12.6625L9.11251 9.99374L6.465 7.35001C6.22062 7.10626 6.22062 6.70623 6.465 6.46248C6.70812 6.21873 7.10437 6.21873 7.34875 6.46248L9.99626 9.11247L12.6825 6.42502C12.9287 6.18127 13.3269 6.18127 13.5731 6.42502C13.8187 6.67502 13.8187 7.06873 13.5731 7.31873L10.8875 10.0063L13.5356 12.65ZM10 0C4.47688 0 0 4.475 0 10C0 15.525 4.47688 20 10 20C15.5231 20 20 15.525 20 10C20 4.475 15.5231 0 10 0Z"
              fill="#F87171"
            />
          </svg>
          <span>{currentError}</span>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 flex w-full items-center gap-3 rounded-[15px] border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-start text-[13px] text-emerald-200 md:w-fit md:text-[14px]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 0C4.47688 0 0 4.475 0 10C0 15.525 4.47688 20 10 20C15.5231 20 20 15.525 20 10C20 4.475 15.5231 0 10 0ZM14.7162 7.55627C15.0537 7.21877 15.0537 6.68127 14.7162 6.34377C14.3787 6.00627 13.8412 6.00627 13.5037 6.34377L8.75 11.0975L6.49625 8.84377C6.15875 8.50627 5.62125 8.50627 5.28375 8.84377C4.94625 9.18127 4.94625 9.71877 5.28375 10.0563L8.19625 12.9688C8.37125 13.1438 8.60375 13.2413 8.8425 13.2413C9.08125 13.2413 9.31375 13.1438 9.48875 12.9688L14.7162 7.55627Z"
              fill="#34D399"
            />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => {
          if (handleValidation()) {
            onSubmit(data);
          }
        })}
        className="w-full"
      >
        {/* Section header */}
        <div className="mb-8 mt-2 flex items-center gap-4 text-start">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#4268FF]/15 text-[#4268FF]">
            <FiShield className="text-2xl" />
          </div>
          <h2 className="text-[16px] font-medium text-white md:text-[18px]">
            Changer de mot de passe
          </h2>
        </div>

        <div className="flex flex-col gap-6">
          {/* Current password — full width */}
          <PasswordField
            label="Mot de passe actuel"
            htmlFor="currentPassword"
            icon={FiLock}
            placeholder="*********"
            hasError={errors.currentPassword}
            inputProps={register("currentPassword")}
          />

          <div className="h-px w-full bg-white/10" />

          {/* New + confirm — side by side */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <PasswordField
              label="Nouveau mot de passe"
              htmlFor="newPassword"
              icon={FiKey}
              placeholder="*****************"
              hasError={errors.newPassword}
              inputProps={register("newPassword")}
            />
            <PasswordField
              label="Comfirmer nouveau mot de passe"
              htmlFor="confirmPassword"
              icon={FiKey}
              placeholder="*****************"
              hasError={errors.confirmPassword}
              inputProps={register("confirmPassword")}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end border-t border-white/10 pt-6">
          <button
            type="submit"
            className="flex h-12 w-full items-center justify-center gap-2.5 rounded-[15px] bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] px-6 text-[14px] font-medium text-white shadow-[0_8px_24px_rgba(66,104,255,0.35)] transition-all duration-200 hover:brightness-110 active:scale-[0.98] md:w-auto md:text-[16px]"
          >
            Confirmer les changements
          </button>
        </div>
      </form>
    </div>
  );
};

export default Securite;